import { Injectable, inject } from '@angular/core';
import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { from, Observable, finalize, tap } from 'rxjs';
import { ContactRegional } from '../../interfaces/contact-regional.interface';
import { EditorialDepartment } from '../../interfaces/editorial-department.interface';
import { PagesService } from '../../services/pages.service';
import { LoadCoreData } from './pages.actions';
import type { CoreDataDto } from '@sal/core/application/dtos/core-data.dto';

export type CoreData = CoreDataDto;

export interface PagesStateModel {
    isLoading: boolean;
    coreData: CoreData | null;
}

@State<PagesStateModel>({
    name: 'pagesState',
    defaults: {
        isLoading: false,
        coreData: null
    }
})
@Injectable()
export class PagesState {
    private readonly pagesService = inject(PagesService);

    @Selector()
    static isLoading(state: PagesStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static coreData(state: PagesStateModel): CoreData | null {
        return state.coreData;
    }

    @Selector()
    static regioncontacts(state: PagesStateModel): ContactRegional[] {
        return state.coreData?.contacts ?? [];
    }

    @Selector()
    static editorialdepartment(state: PagesStateModel): EditorialDepartment[] {
        return state.coreData?.departments ?? [];
    }

    @Selector()
    static lastupload(state: PagesStateModel): string {
        return state.coreData?.settings?.lastUpload ?? '';
    }

    @Action(LoadCoreData)
    public loadCoreData(ctx: StateContext<PagesStateModel>): Observable<CoreData | false> {
        ctx.patchState({ isLoading: true });

        return from(this.pagesService.getCoreData()).pipe(
            tap(data => {
                if (data) {
                    ctx.patchState({ coreData: data });
                }
            }),
            finalize(() => ctx.patchState({ isLoading: false }))
        );
    }
}
